(function () {
  "use strict";

  if (
    window.UpvanityGpoListQuickViewBridge &&
    window.UpvanityGpoListQuickViewBridge.version === 1
  ) return;

  var ELEMENT_NAME = "upv-gpo-list-qv-bridge";
  var TRIGGER_SELECTOR = "[data-upv-gpo-quick-view]";
  var ENDPOINT_MARKER = "data-upv-gpo-option-endpoint";
  var MAP_ASSIGNMENT = "window.GPOConfigs.productOptionSetMap =";
  var THEME_INFO_ASSIGNMENT = "window.GPOConfigs.themeInfo =";
  var OPTION_ASSIGNMENT = /window\.GPOConfigs\.options\[\s*[\"']?(\d+)[\"']?\s*\]\s*=\s*/g;
  var REQUEST_TIMEOUT_MS = 8000;
  var MAX_RESPONSE_CHARS = 1310720;
  var MAX_OPTION_SETS = 100;
  var NON_CONTROL_TYPES = {
    group: true,
    heading: true,
    divider: true,
    modal: true,
    paragraph: true,
    html: true,
    tabs: true,
    spacing: true,
    "size-chart": true
  };
  var loadsByEndpoint = new Map();
  var requirementsByProduct = new Map();
  var verifiedProducts = new Set();
  var pendingIntentGenerations = new Set();
  var scheduledReplayGenerations = new Set();
  var pendingTriggerGenerations = new WeakMap();
  var requestCount = 0;
  var latestIntentGeneration = 0;
  var activeMountGuard = null;
  var unbridgedMountGuardTakeover = null;

  function isPlainObject(value) {
    return !!value && typeof value === "object" && !Array.isArray(value);
  }

  function readJsonValue(source, startIndex) {
    var index = startIndex;
    while (/\s/.test(source.charAt(index))) index += 1;
    var opener = source.charAt(index);
    if (opener !== "{" && opener !== "[") {
      throw new Error("GPO assignment does not begin with JSON");
    }

    var stack = [];
    var inString = false;
    var escaped = false;
    for (var cursor = index; cursor < source.length; cursor += 1) {
      var character = source.charAt(cursor);
      if (inString) {
        if (escaped) escaped = false;
        else if (character === "\\") escaped = true;
        else if (character === '"') inString = false;
        continue;
      }
      if (character === '"') {
        inString = true;
        continue;
      }
      if (character === "{" || character === "[") {
        stack.push(character);
        if (stack.length > 128) throw new Error("GPO JSON nesting is unexpectedly deep");
        continue;
      }
      if (character === "}" || character === "]") {
        var expected = character === "}" ? "{" : "[";
        if (stack.pop() !== expected) throw new Error("Malformed GPO JSON assignment");
        if (!stack.length) {
          var raw = source.slice(index, cursor + 1);
          return { value: JSON.parse(raw), end: cursor + 1 };
        }
      }
    }
    throw new Error("Incomplete GPO JSON assignment");
  }

  function uniqueMarkerIndex(source, marker, label) {
    var index = source.indexOf(marker);
    if (index < 0) throw new Error(label + " is missing");
    if (source.indexOf(marker, index + marker.length) >= 0) {
      throw new Error(label + " is not unique");
    }
    return index;
  }

  function findHtmlTagEnd(source, start) {
    var quote = "";
    for (var index = start; index < source.length; index += 1) {
      var character = source.charAt(index);
      if (quote) {
        if (character === quote) quote = "";
      } else if (character === '"' || character === "'") {
        quote = character;
      } else if (character === ">") {
        return index;
      }
    }
    return -1;
  }

  function findGpoScriptRange(source, markerIndex) {
    var tags = /<!--|<\/?[A-Za-z]/g;
    var match;
    while ((match = tags.exec(source))) {
      if (match.index > markerIndex) return null;
      if (match[0] === "<!--") {
        var commentEnd = source.indexOf("-->", tags.lastIndex);
        if (commentEnd < 0) return null;
        tags.lastIndex = commentEnd + 3;
        continue;
      }
      var tagName = /^<\/?([A-Za-z][A-Za-z0-9:-]*)/.exec(source.slice(match.index));
      var tagEnd = findHtmlTagEnd(source, match.index + tagName[0].length);
      if (tagEnd < 0) return null;
      tags.lastIndex = tagEnd + 1;
      var name = tagName[1].toLowerCase();
      if (source.charAt(match.index + 1) === "/" ||
        !/^(script|style|textarea|title|xmp|iframe|noembed|noframes)$/.test(name)) continue;
      var closingTag = new RegExp("<\\/" + name + "(?=[\\s/>])", "gi");
      closingTag.lastIndex = tags.lastIndex;
      var close = closingTag.exec(source);
      if (!close) return null;
      var closingEnd = findHtmlTagEnd(source, closingTag.lastIndex);
      if (closingEnd < 0) return null;
      if (markerIndex >= tags.lastIndex && markerIndex < close.index) {
        if (name !== "script") return null;
        var attributes = source.slice(match.index + tagName[0].length, tagEnd);
        var attributePattern = /\s+([^\s"'<>/=]+)(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'`=<>]+))?/g;
        var attributeCursor = 0;
        while (attributeCursor < attributes.length) {
          if (/^\s*\/?\s*$/.test(attributes.slice(attributeCursor))) break;
          attributePattern.lastIndex = attributeCursor;
          var attribute = attributePattern.exec(attributes);
          if (!attribute || attribute.index !== attributeCursor) return null;
          if (attribute[1].toLowerCase() === "src") return null;
          attributeCursor = attributePattern.lastIndex;
        }
        return { start: tags.lastIndex, end: close.index };
      }
      tags.lastIndex = closingEnd + 1;
    }
    return null;
  }

  function normalizeOptionSetIds(value) {
    var values = Array.isArray(value) ? value : String(value == null ? "" : value).split(",");
    var result = [];
    values.forEach(function (candidate) {
      var id = String(candidate).trim();
      if (!id) return;
      if (!/^\d+$/.test(id)) throw new Error("GPO option map contains an invalid identifier");
      if (result.indexOf(id) < 0) result.push(id);
    });
    return result;
  }

  function deriveDefinitionContract(optionSetId, definition) {
    if (!isPlainObject(definition) || !Array.isArray(definition.elements)) {
      throw new Error("GPO option definition is invalid");
    }
    var groupCount = 0;
    var nodeCount = 0;
    var elementIds = [];
    var requiredIds = [];
    function walk(elements, depth) {
      if (depth > 32 || elements.length > 1000) {
        throw new Error("GPO option structure is unexpectedly deep");
      }
      elements.forEach(function (element) {
        if (!isPlainObject(element)) throw new Error("GPO option element is invalid");
        if (element.hidden === true) return;
        var type = String(element.type || "").trim();
        var id = String(element.id || "").trim();
        if (!type || !id || !/^[A-Za-z0-9_-]{1,128}$/.test(id)) {
          throw new Error("GPO option element identity is invalid");
        }
        nodeCount += 1;
        if (nodeCount > 2000) throw new Error("GPO option structure is unexpectedly large");
        if (type === "group") {
          if (!Array.isArray(element.elements)) throw new Error("GPO option group is invalid");
          groupCount += 1;
          walk(element.elements, depth + 1);
          return;
        }
        if (NON_CONTROL_TYPES[type]) return;
        if (elementIds.indexOf(id) < 0) elementIds.push(id);
        if (element.required === true && requiredIds.indexOf(id) < 0) requiredIds.push(id);
      });
    }
    walk(definition.elements, 0);
    if (!elementIds.length) throw new Error("GPO option definition contains no controls");
    return {
      optionSetId: String(optionSetId),
      groupCount: groupCount,
      nodeCount: nodeCount,
      elementCount: elementIds.length,
      elementIds: elementIds,
      requiredIds: requiredIds
    };
  }

  function parsePayload(html, productId) {
    var normalizedProductId = String(productId || "").trim();
    if (!/^\d+$/.test(normalizedProductId)) throw new Error("Product identity is invalid");
    if (typeof html !== "string" || !html || html.length > MAX_RESPONSE_CHARS) {
      throw new Error("GPO endpoint response has an invalid size");
    }
    var mapIndex = uniqueMarkerIndex(html, MAP_ASSIGNMENT, "GPO product map assignment");
    var themeInfoIndex = uniqueMarkerIndex(html, THEME_INFO_ASSIGNMENT, "GPO theme boundary");
    var endpointIndex = uniqueMarkerIndex(html, ENDPOINT_MARKER, "GPO endpoint marker");
    if (!(mapIndex < themeInfoIndex)) {
      throw new Error("GPO response markers are out of order");
    }
    var endpointTagStart = html.lastIndexOf("<span", endpointIndex);
    var endpointTagEnd = html.indexOf(">", endpointIndex);
    var endpointClose = html.indexOf("</span>", endpointTagEnd);
    if (endpointTagStart < 0 || endpointTagEnd < 0 || endpointClose < endpointTagEnd) {
      throw new Error("GPO endpoint marker is incomplete");
    }
    var endpointTag = html.slice(endpointTagStart, endpointTagEnd + 1);
    var endpointPattern = new RegExp(
      ENDPOINT_MARKER + "[^>]*data-product-id=[\\\"']" + normalizedProductId + "[\\\"']"
    );
    if (!endpointPattern.test(endpointTag)) throw new Error("GPO endpoint product identity does not match");

    var scriptRange = findGpoScriptRange(html, mapIndex);
    if (!scriptRange || themeInfoIndex < scriptRange.start || themeInfoIndex >= scriptRange.end) {
      throw new Error("GPO assignments must share a complete inline script");
    }
    var source = html.slice(scriptRange.start, scriptRange.end);
    var themeInfoResult = readJsonValue(source, themeInfoIndex - scriptRange.start + THEME_INFO_ASSIGNMENT.length);
    if (!isPlainObject(themeInfoResult.value)) throw new Error("GPO theme boundary is invalid");
    var mapResult = readJsonValue(source, mapIndex - scriptRange.start + MAP_ASSIGNMENT.length);
    if (!isPlainObject(mapResult.value)) throw new Error("GPO product map is invalid");

    var relevantMap = Object.create(null);
    var requiredIds = [];
    ["all", "automate", normalizedProductId].forEach(function (key) {
      if (!Object.prototype.hasOwnProperty.call(mapResult.value, key)) return;
      var ids = normalizeOptionSetIds(mapResult.value[key]);
      if (!ids.length) return;
      relevantMap[key] = ids.join(",");
      ids.forEach(function (id) {
        if (requiredIds.indexOf(id) < 0) requiredIds.push(id);
      });
    });
    if (!requiredIds.length) throw new Error("No GPO option sets apply to this product");
    if (requiredIds.length > MAX_OPTION_SETS) throw new Error("GPO option map is unexpectedly large");

    var definitions = Object.create(null);
    var totalElementCount = 0;
    var match;
    OPTION_ASSIGNMENT.lastIndex = 0;
    while ((match = OPTION_ASSIGNMENT.exec(source))) {
      var optionId = match[1];
      var definitionResult = readJsonValue(source, OPTION_ASSIGNMENT.lastIndex);
      OPTION_ASSIGNMENT.lastIndex = definitionResult.end;
      if (requiredIds.indexOf(optionId) < 0) continue;
      if (!isPlainObject(definitionResult.value) || !Array.isArray(definitionResult.value.elements)) {
        throw new Error("GPO option definition is invalid");
      }
      definitions[optionId] = definitionResult.value;
      totalElementCount += definitionResult.value.elements.length;
    }
    requiredIds.forEach(function (id) {
      if (!definitions[id]) throw new Error("A required GPO option definition is missing");
    });
    if (!totalElementCount) throw new Error("GPO option definitions contain no fields");
    var contracts = Object.create(null);
    requiredIds.forEach(function (id) {
      contracts[id] = deriveDefinitionContract(id, definitions[id]);
    });
    return {
      productId: normalizedProductId,
      map: relevantMap,
      requiredIds: requiredIds,
      definitions: definitions,
      contracts: contracts
    };
  }

  function buildEndpoint(productUrl, viewName) {
    var url = new URL(productUrl, document.baseURI);
    if (
      url.origin !== window.location.origin ||
      !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)
    ) {
      throw new Error("Quick View product URL is outside the storefront");
    }
    var view = String(viewName || "").trim();
    if (!/^[a-z0-9][a-z0-9-]{0,63}$/.test(view)) throw new Error("GPO endpoint view is invalid");
    url.search = "";
    url.hash = "";
    url.searchParams.set("view", view);
    return url;
  }

  function streamPayloadIsComplete(source, productId) {
    var mapIndex = source.indexOf(MAP_ASSIGNMENT);
    var themeInfoIndex = source.indexOf(THEME_INFO_ASSIGNMENT);
    var endpointIndex = source.indexOf(ENDPOINT_MARKER);
    if (!(mapIndex >= 0 && mapIndex < themeInfoIndex && endpointIndex >= 0)) return false;
    var scriptRange = findGpoScriptRange(source, mapIndex);
    if (!scriptRange || themeInfoIndex < scriptRange.start || themeInfoIndex >= scriptRange.end) return false;
    var endpointTagEnd = source.indexOf(">", endpointIndex);
    var endpointClose = source.indexOf("</span>", endpointTagEnd);
    if (endpointTagEnd < 0 || endpointClose < endpointTagEnd) return false;
    var endpointPattern = new RegExp(
      ENDPOINT_MARKER + "[^>]*data-product-id=[\\\"']" + String(productId) + "[\\\"']"
    );
    if (!endpointPattern.test(source.slice(endpointIndex, endpointTagEnd + 1))) return false;
    try {
      readJsonValue(source.slice(scriptRange.start, scriptRange.end),
        themeInfoIndex - scriptRange.start + THEME_INFO_ASSIGNMENT.length);
    } catch (_error) {
      return false;
    }
    return true;
  }

  function readResponseText(response, productId) {
    var Decoder = window.TextDecoder || (typeof TextDecoder === "function" ? TextDecoder : null);
    var reader = response.body && typeof response.body.getReader === "function"
      ? response.body.getReader()
      : null;
    if (!reader || !Decoder) {
      return response.text().then(function (html) {
        if (typeof html !== "string" || html.length > MAX_RESPONSE_CHARS) {
          throw new Error("GPO endpoint response has an invalid decoded size");
        }
        return html;
      });
    }

    var decoder = new Decoder("utf-8", { fatal: false });
    var source = "";
    function readNext() {
      return reader.read().then(function (result) {
        if (result.done) {
          source += decoder.decode();
          return source;
        }
        source += decoder.decode(result.value, { stream: true });
        if (source.length > MAX_RESPONSE_CHARS) {
          return Promise.resolve(reader.cancel()).catch(function () {}).then(function () {
            throw new Error("GPO endpoint response has an invalid decoded size");
          });
        }
        if (streamPayloadIsComplete(source, productId)) {
          source += decoder.decode();
          return Promise.resolve(reader.cancel()).catch(function () {}).then(function () {
            return source;
          });
        }
        return readNext();
      });
    }
    return readNext();
  }

  function fetchPayload(endpoint, productId) {
    var key = endpoint.href + "|" + String(productId);
    if (loadsByEndpoint.has(key)) return loadsByEndpoint.get(key);
    var controller = typeof AbortController === "function" ? new AbortController() : null;
    var timeoutId;
    requestCount += 1;
    var timeout = new Promise(function (_resolve, reject) {
      timeoutId = window.setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error("GPO option request timed out"));
      }, REQUEST_TIMEOUT_MS);
    });
    var request = window.fetch(endpoint.href, {
      credentials: "same-origin",
      headers: { Accept: "text/html" },
      signal: controller ? controller.signal : undefined
    }).then(function (response) {
      if (!response || !response.ok) throw new Error("GPO option request failed");
      var contentType = response.headers && response.headers.get
        ? String(response.headers.get("content-type") || "")
        : "";
      if (contentType && contentType.indexOf("text/html") < 0) {
        throw new Error("GPO option response is not HTML");
      }
      var contentLength = response.headers && response.headers.get
        ? Number(response.headers.get("content-length"))
        : 0;
      if (Number.isFinite(contentLength) && contentLength > MAX_RESPONSE_CHARS) {
        throw new Error("GPO option response is too large");
      }
      return readResponseText(response, productId);
    }).then(function (html) {
      return parsePayload(html, productId);
    });
    var pending = Promise.race([request, timeout]).then(function (payload) {
      window.clearTimeout(timeoutId);
      return payload;
    }, function (error) {
      window.clearTimeout(timeoutId);
      throw error;
    });
    loadsByEndpoint.set(key, pending);
    pending.then(function () {
      if (loadsByEndpoint.get(key) === pending) loadsByEndpoint.delete(key);
    }, function () {
      if (loadsByEndpoint.get(key) === pending) loadsByEndpoint.delete(key);
    });
    return pending;
  }

  function mergePayload(payload) {
    var config = window.GPOConfigs;
    if (!isPlainObject(config)) throw new Error("GPO storefront configuration is unavailable");
    if (!config.options || typeof config.options !== "object") config.options = [];
    if (!isPlainObject(config.productOptionSetMap)) config.productOptionSetMap = {};
    Object.keys(payload.map).forEach(function (key) {
      config.productOptionSetMap[key] = payload.map[key];
    });
    payload.requiredIds.forEach(function (id) {
      config.options[id] = payload.definitions[id];
    });
  }

  function cacheRequirements(key, payload) {
    var contracts = Object.create(null);
    var mandatoryIds = [];
    ["all", payload.productId].forEach(function (mapKey) {
      if (!Object.prototype.hasOwnProperty.call(payload.map, mapKey)) return;
      normalizeOptionSetIds(payload.map[mapKey]).forEach(function (id) {
        if (mandatoryIds.indexOf(id) < 0) mandatoryIds.push(id);
      });
    });
    payload.requiredIds.forEach(function (id) {
      var contract = payload.contracts[id];
      contracts[id] = {
        optionSetId: contract.optionSetId,
        groupCount: contract.groupCount,
        nodeCount: contract.nodeCount,
        elementCount: contract.elementCount,
        elementIds: contract.elementIds.slice(),
        requiredIds: contract.requiredIds.slice()
      };
    });
    requirementsByProduct.set(key, {
      map: Object.assign({}, payload.map),
      requiredIds: payload.requiredIds.slice(),
      mandatoryIds: mandatoryIds,
      contracts: contracts
    });
  }

  function requirementsAreLive(requirements) {
    var config = window.GPOConfigs;
    if (
      !requirements ||
      !isPlainObject(config) ||
      !config.options ||
      !isPlainObject(config.productOptionSetMap)
    ) return false;
    var mapMatches = Object.keys(requirements.map).every(function (key) {
      return String(config.productOptionSetMap[key]) === String(requirements.map[key]);
    });
    if (!mapMatches) return false;
    var definitionsMatch = requirements.requiredIds.every(function (id) {
      var definition = config.options[id];
      if (!isPlainObject(definition) || !Array.isArray(definition.elements)) return false;
      var liveContract;
      try {
        liveContract = deriveDefinitionContract(id, definition);
      } catch (_error) {
        return false;
      }
      var expected = requirements.contracts[id];
      return !!expected &&
        liveContract.groupCount === expected.groupCount &&
        liveContract.nodeCount === expected.nodeCount &&
        liveContract.elementCount === expected.elementCount &&
        liveContract.elementIds.join(",") === expected.elementIds.join(",") &&
        liveContract.requiredIds.join(",") === expected.requiredIds.join(",");
    });
    return definitionsMatch;
  }

  function productKey(productId, productUrl) {
    return String(productId) + "|" + productUrl.pathname;
  }

  function mountGuardOwnsCurrentDrawer(guard) {
    var drawer = document.getElementById("yv_quickView_product");
    return !!guard &&
      !!drawer &&
      !!drawer.classList &&
      drawer.classList.contains("yv_quickView_product") &&
      drawer.upvQuickViewTrigger === guard.trigger;
  }

  function mountGuardTriggerIdentityIsCurrent(guard) {
    if (
      !guard ||
      !guard.trigger ||
      guard.trigger.getAttribute("data-upv-gpo-quick-view") === null ||
      String(guard.trigger.getAttribute("data-product-id")) !== guard.productId
    ) return false;
    var currentProductUrl;
    try {
      currentProductUrl = new URL(
        guard.trigger.getAttribute("data-href") || guard.trigger.href,
        document.baseURI
      );
    } catch (_error) {
      return false;
    }
    return currentProductUrl.origin === window.location.origin &&
      productKey(guard.productId, currentProductUrl) === guard.key;
  }

  function currentTriggerProductIdentity(trigger) {
    if (!trigger || typeof trigger.getAttribute !== "function") return null;
    var productId = String(trigger.getAttribute("data-product-id") || "").trim();
    if (!productId) return null;
    var productUrl;
    try {
      productUrl = new URL(
        trigger.getAttribute("data-href") || trigger.href,
        document.baseURI
      );
    } catch (_error) {
      return null;
    }
    if (productUrl.origin !== window.location.origin) return null;
    return {
      key: productKey(productId, productUrl),
      productId: productId
    };
  }

  function replayGuardOwnsCurrentTrigger(guard) {
    if (
      !guard ||
      !guard.bridge ||
      guard.bridge.isConnected === false ||
      typeof guard.bridge.contains !== "function" ||
      !guard.bridge.contains(guard.trigger) ||
      guard.trigger.isConnected !== true ||
      !mountGuardTriggerIdentityIsCurrent(guard)
    ) return false;
    return true;
  }

  function setBusy(trigger, busy) {
    if (busy) {
      trigger.setAttribute("aria-busy", "true");
      trigger.classList.add("upv-gpo-quick-view-link--loading");
    } else {
      trigger.removeAttribute("aria-busy");
      trigger.classList.remove("upv-gpo-quick-view-link--loading");
    }
  }

  function replayQuickView(trigger, key, productId, productUrl, bridge) {
    var requirements = requirementsByProduct.get(key);
    if (!requirements || !requirementsAreLive(requirements)) {
      throw new Error("GPO mount requirements are unavailable");
    }
    var guard = {
      generation: latestIntentGeneration,
      trigger: trigger,
      key: key,
      productId: String(productId),
      productUrl: productUrl,
      bridge: bridge,
      requirements: requirements,
      ready: false
    };
    scheduledReplayGenerations.add(guard.generation);
    window.setTimeout(function () {
      var ownsReplay = scheduledReplayGenerations.has(guard.generation) &&
        guard.generation === latestIntentGeneration &&
        replayGuardOwnsCurrentTrigger(guard);
      scheduledReplayGenerations.delete(guard.generation);
      if (!ownsReplay) return;
      var previousMountGuard = activeMountGuard;
      activeMountGuard = guard;
      trigger.setAttribute("data-upv-gpo-replay", "true");
      try {
        trigger.click();
      } finally {
        if (trigger.getAttribute("data-upv-gpo-replay") === "true") {
          trigger.removeAttribute("data-upv-gpo-replay");
        }
        var replayClaimedDrawer = mountGuardOwnsCurrentDrawer(guard);
        var currentGuardOwnsDrawer = mountGuardOwnsCurrentDrawer(activeMountGuard);
        if (
          replayClaimedDrawer &&
          (
            !currentGuardOwnsDrawer ||
            activeMountGuard.generation <= guard.generation
          )
        ) {
          activeMountGuard = guard;
        } else if (
          !replayClaimedDrawer &&
          (activeMountGuard === guard || !currentGuardOwnsDrawer)
        ) {
          activeMountGuard = mountGuardOwnsCurrentDrawer(previousMountGuard)
            ? previousMountGuard
            : null;
        }
      }
    }, 0);
  }

  function navigateToProduct(url) {
    window.location.assign(url.href);
  }

  function beginQuickViewIntent() {
    scheduledReplayGenerations.delete(latestIntentGeneration);
    latestIntentGeneration += 1;
    return latestIntentGeneration;
  }

  function currentQuickViewIntentIsPending() {
    return pendingIntentGenerations.has(latestIntentGeneration) ||
      scheduledReplayGenerations.has(latestIntentGeneration);
  }

  function requiredControlExists(gpoApp, elementId) {
    var nodes = gpoApp.querySelectorAll('[data-element-id="' + elementId + '"]');
    for (var index = 0; index < nodes.length; index += 1) {
      var node = nodes[index];
      if (
        /^(INPUT|SELECT|TEXTAREA)$/.test(String(node.tagName || "")) ||
        (typeof node.querySelector === "function" && node.querySelector("input, select, textarea"))
      ) return true;
    }
    return false;
  }

  function inspectGuardedMount(guard, form) {
    if (!guard) return { ready: false, reason: "guard" };
    if (!requirementsAreLive(guard.requirements)) {
      return { ready: false, reason: "requirements" };
    }
    var drawer = document.getElementById("yv_quickView_product");
    if (
      !drawer ||
      !drawer.classList ||
      !drawer.classList.contains("yv_quickView_product") ||
      drawer.upvQuickViewTrigger !== guard.trigger ||
      guard.trigger.getAttribute("data-upv-gpo-quick-view") === null ||
      String(guard.trigger.getAttribute("data-product-id")) !== guard.productId
    ) return { ready: false, reason: "drawer" };
    if (
      !form ||
      form.getAttribute("id") !== "product-form-" + guard.productId ||
      typeof drawer.contains !== "function" ||
      !drawer.contains(form)
    ) return { ready: false, reason: "form" };

    var gpoContainer = form.querySelector(".gpo-container");
    var gpoApp = form.querySelector(".gpo-container .gpo-app");
    if (
      !gpoContainer ||
      !gpoApp ||
      (!(gpoApp.childElementCount > 0) && !(gpoApp.children && gpoApp.children.length))
    ) return { ready: false, reason: "surface" };

    var markers = form.querySelectorAll('input[name="properties[_has_gpo]"]');
    if (markers.length !== 1 || markers[0].disabled) return { ready: false, reason: "marker" };
    var markerOptionSetIds;
    var mountedOptionSetIds;
    try {
      markerOptionSetIds = normalizeOptionSetIds(markers[0].value);
      mountedOptionSetIds = normalizeOptionSetIds(gpoApp.getAttribute("data-option-set-id"));
    } catch (_error) {
      return { ready: false, reason: "marker" };
    }
    if (!mountedOptionSetIds.length) return { ready: false, reason: "marker" };
    var optionSetIds = markerOptionSetIds.length ? markerOptionSetIds : mountedOptionSetIds;
    var markerMatchesMount = !markerOptionSetIds.length ||
      (
        markerOptionSetIds.length === mountedOptionSetIds.length &&
        markerOptionSetIds.every(function (optionSetId) {
          return mountedOptionSetIds.indexOf(optionSetId) >= 0;
        })
      );
    if (!markerMatchesMount) return { ready: false, reason: "option-set" };
    if (!optionSetIds.length) return { ready: false, reason: "marker" };
    var mandatoryOptionSetsPresent = guard.requirements.mandatoryIds.every(function (optionSetId) {
      return optionSetIds.indexOf(optionSetId) >= 0;
    });
    if (!mandatoryOptionSetsPresent) return { ready: false, reason: "option-set" };
    var contracts = [];
    var optionSetsValid = optionSetIds.every(function (optionSetId) {
      var contract = guard.requirements.contracts[optionSetId];
      if (!contract || guard.requirements.requiredIds.indexOf(optionSetId) < 0) return false;
      contracts.push(contract);
      return true;
    });
    if (!optionSetsValid) return { ready: false, reason: "option-set" };

    var expectedGroupCount = contracts.reduce(function (count, contract) {
      return count + contract.groupCount;
    }, 0);
    var groupCount = gpoApp.querySelectorAll(".gpo-group").length;
    if (groupCount < expectedGroupCount) return { ready: false, reason: "groups" };
    var elementNodes = gpoApp.querySelectorAll("[data-element-id]");
    var elementIds = new Set();
    for (var elementIndex = 0; elementIndex < elementNodes.length; elementIndex += 1) {
      var elementId = String(elementNodes[elementIndex].getAttribute("data-element-id") || "").trim();
      if (elementId) elementIds.add(elementId);
    }
    var elementsComplete = contracts.every(function (contract) {
      return contract.elementIds.every(function (elementId) {
        return elementIds.has(elementId);
      });
    });
    if (!elementsComplete) return { ready: false, reason: "elements" };
    var requiredComplete = contracts.every(function (contract) {
      return contract.requiredIds.every(function (elementId) {
        return elementIds.has(elementId) && requiredControlExists(gpoApp, elementId);
      });
    });
    if (!requiredComplete) return { ready: false, reason: "required" };
    return { ready: true, reason: "ready", optionSetIds: optionSetIds };
  }

  function removeMountError(form) {
    var error = form && form.querySelector("[data-upv-gpo-mount-error]");
    if (error && typeof error.remove === "function") error.remove();
  }

  function showMountError(form, guard) {
    var error = form.querySelector("[data-upv-gpo-mount-error]");
    if (error) return error;
    error = document.createElement("div");
    error.setAttribute("data-upv-gpo-mount-error", "");
    error.setAttribute("class", "productErrors error-text");
    error.setAttribute("role", "alert");
    error.setAttribute("aria-live", "assertive");
    var message = document.createElement("span");
    message.textContent = "Customization options are still loading. Please wait, or ";
    var link = document.createElement("a");
    link.href = guard.productUrl.href;
    link.textContent = "open the full product page to continue safely";
    error.appendChild(message);
    error.appendChild(link);
    form.appendChild(error);
    return error;
  }

  function guardedFormFromEvent(event, requireSubmitControl) {
    var guard = activeMountGuard;
    if (!guard) return null;
    var target = event.target;
    if (!target) return null;
    if (requireSubmitControl) {
      if (typeof target.closest !== "function") return null;
      var submitControl = target.closest(
        '.Sd_addProduct, [data-gpo-product-atc], button[type="submit"], input[type="submit"]'
      );
      if (!submitControl) return null;
    }
    var form = String(target.tagName || "") === "FORM"
      ? target
      : (typeof target.closest === "function" ? target.closest("form") : null);
    var drawer = document.getElementById("yv_quickView_product");
    var guardOwnsForm = form &&
      drawer &&
      drawer.upvQuickViewTrigger === guard.trigger &&
      typeof drawer.contains === "function" &&
      drawer.contains(form);
    if (!guardOwnsForm) return null;
    var takeover = unbridgedMountGuardTakeover;
    var currentIdentity = takeover && currentTriggerProductIdentity(takeover.trigger);
    if (
      takeover &&
      takeover.guard === guard &&
      takeover.trigger === guard.trigger &&
      guard.trigger.getAttribute("data-upv-gpo-quick-view") === null &&
      currentIdentity &&
      currentIdentity.productId === takeover.productId &&
      currentIdentity.key === takeover.key &&
      takeover.productId !== guard.productId &&
      takeover.key !== guard.key &&
      form.getAttribute("id") === "product-form-" + takeover.productId
    ) {
      activeMountGuard = null;
      unbridgedMountGuardTakeover = null;
      return null;
    }
    return form;
  }

  function guardMountSubmission(event, requireSubmitControl) {
    var form = guardedFormFromEvent(event, requireSubmitControl);
    if (!form) return;
    var inspection = inspectGuardedMount(activeMountGuard, form);
    if (inspection.ready) {
      activeMountGuard.ready = true;
      removeMountError(form);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
    showMountError(form, activeMountGuard);
  }

  function handleGpoRenderCompleted(event) {
    var guard = activeMountGuard;
    var productId = String(event.detail && event.detail.product && event.detail.product.id || "");
    var form = event.detail && event.detail.form;
    if (!guard || productId !== guard.productId) return;
    var inspection = inspectGuardedMount(guard, form);
    guard.ready = inspection.ready;
    if (inspection.ready) removeMountError(form);
  }

  function trackUnbridgedQuickViewIntent(event) {
    var eventTarget = event.target;
    if (!eventTarget || typeof eventTarget.closest !== "function") return;
    if (eventTarget.closest(TRIGGER_SELECTOR)) return;
    var quickViewTrigger = eventTarget.closest(".quickView");
    var takeover = null;
    if (
      quickViewTrigger &&
      activeMountGuard &&
      quickViewTrigger === activeMountGuard.trigger &&
      !mountGuardTriggerIdentityIsCurrent(activeMountGuard)
    ) {
      var currentIdentity = currentTriggerProductIdentity(quickViewTrigger);
      if (
        currentIdentity &&
        currentIdentity.productId !== activeMountGuard.productId &&
        currentIdentity.key !== activeMountGuard.key
      ) {
        takeover = {
          guard: activeMountGuard,
          key: currentIdentity.key,
          productId: currentIdentity.productId,
          trigger: quickViewTrigger
        };
      }
    }
    if (
      quickViewTrigger ||
      (currentQuickViewIntentIsPending() && event.isTrusted !== false)
    ) {
      beginQuickViewIntent();
      if (takeover) unbridgedMountGuardTakeover = takeover;
    }
  }

  function cancelPendingQuickViewOnEscape(event) {
    if (
      (event.key === "Escape" || event.keyCode === 27) &&
      currentQuickViewIntentIsPending()
    ) beginQuickViewIntent();
  }

  if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
    document.addEventListener("keydown", cancelPendingQuickViewOnEscape, true);
    document.addEventListener("click", trackUnbridgedQuickViewIntent, true);
    document.addEventListener("click", function (event) {
      guardMountSubmission(event, true);
    }, true);
    document.addEventListener("submit", function (event) {
      guardMountSubmission(event, false);
    }, true);
    document.addEventListener("gpoRenderCompleted", handleGpoRenderCompleted);
  }

  var api = Object.freeze({
    version: 1,
    parsePayload: parsePayload,
    buildEndpoint: buildEndpoint,
    deriveDefinitionContract: deriveDefinitionContract,
    inspectGuardedMount: function (form) {
      return inspectGuardedMount(activeMountGuard, form);
    },
    getState: function () {
      return {
        requestCount: requestCount,
        latestIntentGeneration: latestIntentGeneration,
        verifiedProducts: Array.from(verifiedProducts),
        cachedProducts: Array.from(requirementsByProduct.keys())
      };
    }
  });
  window.UpvanityGpoListQuickViewBridge = api;

  if (!window.customElements || typeof HTMLElement === "undefined") return;
  if (window.customElements.get(ELEMENT_NAME)) return;

  class UpvanityGpoListQuickViewBridge extends HTMLElement {
    constructor() {
      super();
      this.bridgeConnected = false;
      this.intentGeneration = 0;
      this.handleBridgeClick = this.handleBridgeClick.bind(this);
    }

    connectedCallback() {
      if (this.bridgeConnected || typeof window.fetch !== "function") return;
      this.bridgeConnected = true;
      this.addEventListener("click", this.handleBridgeClick, true);
      this.querySelectorAll(TRIGGER_SELECTOR).forEach(function (trigger) {
        trigger.classList.add("quickView");
      });
      this.setAttribute("data-upv-gpo-bridge-ready", "true");
    }

    disconnectedCallback() {
      if (
        this.intentGeneration === latestIntentGeneration &&
        currentQuickViewIntentIsPending()
      ) beginQuickViewIntent();
      if (
        activeMountGuard &&
        typeof this.contains === "function" &&
        this.contains(activeMountGuard.trigger) &&
        !mountGuardOwnsCurrentDrawer(activeMountGuard)
      ) activeMountGuard = null;
      if (!this.bridgeConnected) return;
      this.removeEventListener("click", this.handleBridgeClick, true);
      this.bridgeConnected = false;
      this.removeAttribute("data-upv-gpo-bridge-ready");
    }

    handleBridgeClick(event) {
      var eventTarget = event.target;
      var trigger = eventTarget && typeof eventTarget.closest === "function"
        ? eventTarget.closest(TRIGGER_SELECTOR)
        : null;
      if (!trigger || !this.contains(trigger)) return;
      if (trigger.getAttribute("data-upv-gpo-replay") === "true") {
        trigger.removeAttribute("data-upv-gpo-replay");
        return;
      }
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) {
        if (currentQuickViewIntentIsPending()) {
          this.intentGeneration = beginQuickViewIntent();
        }
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
        return;
      }

      var productId = String(trigger.getAttribute("data-product-id") || "").trim();
      var productUrl;
      try {
        productUrl = new URL(trigger.getAttribute("data-href") || trigger.href, document.baseURI);
      } catch (_error) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();

      var key = productKey(productId, productUrl);
      if (verifiedProducts.has(key)) {
        if (requirementsAreLive(requirementsByProduct.get(key))) {
          try {
            this.intentGeneration = beginQuickViewIntent();
            replayQuickView(trigger, key, productId, productUrl, this);
          } catch (error) {
            verifiedProducts.delete(key);
            requirementsByProduct.delete(key);
            navigateToProduct(productUrl);
          }
          return;
        }
        verifiedProducts.delete(key);
        requirementsByProduct.delete(key);
      }
      var intentGeneration = beginQuickViewIntent();
      this.intentGeneration = intentGeneration;
      pendingIntentGenerations.add(intentGeneration);
      pendingTriggerGenerations.set(trigger, intentGeneration);
      setBusy(trigger, true);

      var retirePendingIntent = function () {
        pendingIntentGenerations.delete(intentGeneration);
        if (pendingTriggerGenerations.get(trigger) === intentGeneration) {
          pendingTriggerGenerations.delete(trigger);
          setBusy(trigger, false);
        }
      };

      var endpoint;
      try {
        endpoint = buildEndpoint(productUrl.href, this.getAttribute("data-option-view"));
      } catch (error) {
        retirePendingIntent();
        navigateToProduct(productUrl);
        return;
      }

      Promise.resolve().then(function () {
        return fetchPayload(endpoint, productId);
      }).then(function (payload) {
        if (
          intentGeneration === latestIntentGeneration &&
          this.isConnected !== false &&
          trigger.isConnected !== false
        ) {
          mergePayload(payload);
          cacheRequirements(key, payload);
          verifiedProducts.add(key);
          replayQuickView(trigger, key, productId, productUrl, this);
        }
      }.bind(this)).catch(function (error) {
        if (window.console && typeof window.console.warn === "function") {
          window.console.warn("Collection List options were unavailable; opening the product page.", error);
        }
        if (
          intentGeneration === latestIntentGeneration &&
          this.isConnected !== false &&
          trigger.isConnected !== false
        ) navigateToProduct(productUrl);
      }.bind(this)).then(function () {
        retirePendingIntent();
      }, retirePendingIntent);
    }
  }

  window.customElements.define(ELEMENT_NAME, UpvanityGpoListQuickViewBridge);
})();
